import { useState, useEffect, useRef } from 'react';
import useApi from '../../api';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import FilesList from './FilesList';
import useAnalytics from '../../analytics/analytics';
import { useUser } from '../../user/UserContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { File, ChevronRight, ChevronDown, Clock, Trash2 } from 'lucide-react';
import { formatRelativeTime } from '../../utils/dateFormatter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Card, CardContent } from "@/components/ui/card"
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

function FileSkeleton({ expanded = false }: { expanded?: boolean }) {
    return (
        <div className="flex flex-col w-full p-3 rounded-md border shadow-sm">
            <div className="w-full animate-pulse">
                <div className="flex flex-row justify-between w-full items-center">
                    <div className="flex items-center overflow-hidden flex-1">
                        <div className="h-5 w-5 bg-muted rounded-full mr-3 flex-shrink-0"></div>
                        <div className="flex items-center overflow-hidden flex-1">
                            <div className="h-4 w-48 bg-muted rounded"></div>
                            <div className="ml-auto pl-2 h-3 w-24 bg-muted rounded"></div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                        <div className="h-6 w-16 bg-muted rounded-md"></div>
                        <div className="h-7 w-7 bg-muted rounded-md"></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Files({ 
    agentId,
    agentName
}: { 
    agentId: string,
    agentName: string
}) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [files, setFiles] = useState<any[]>([]);
    const { get, post, remove } = useApi();
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showFiles, setShowFiles] = useState(false);
    const [filesSelected, setFilesSelected] = useState(false);
    const [selectedFileContent, setSelectedFileContent] = useState<string>('');
    const [isFileModalOpen, setIsFileModalOpen] = useState(false);
    const [selectedFileChunks, setSelectedFileChunks] = useState<any[]>([]);
    const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
    const { captureEvent } = useAnalytics();
    const { checkLicenseType } = useUser();
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (agentId) {
            get(`/files/?agent_id=${agentId}`).then((res) => {
                setFiles(res.data);
                setLoading(false);
            }).catch((error) => {
                console.error('Error fetching files:', error);
                setLoading(false);
            });
        }
    }, [agentId]);

    useEffect(() => {
        if (!showFiles && filesSelected && agentId) {
            get(`/files/?agent_id=${agentId}`).then((res) => {
                setFiles(res.data);
                setLoading(false);
                setFilesSelected(false);
            }).catch((error) => {
                console.error('Error fetching files:', error);
                setLoading(false);
            });
        }
    }, [showFiles]);

    function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        if (event.target.files && event.target.files[0]) {
            setSelectedFile(event.target.files[0] as any);
        }
    }

    function handleUploadFile() {
        if (selectedFile) {
            setUploading(true);
            const formData = new FormData();
            formData.append('files', selectedFile);
            formData.append('type', 'file');

            post(`/documents/file?agent_id=${agentId}`, formData)
                .then(response => {
                    toast.success('File processing started', toastConfig);
                    setUploading(false);
                    setSelectedFile(null);
                    
                    captureEvent('file_uploaded', {
                        agent_id: agentId,
                        file_name: selectedFile.name
                    });
                    
                    // Refresh file list after a short delay
                    setTimeout(() => {
                        get(`/files/?agent_id=${agentId}`).then((res) => {
                            setFiles(res.data);
                        }).catch((error) => {
                            console.error('Error fetching files:', error);
                        });
                    }, 1000);
                })
                .catch(error => {
                    console.error('Error uploading file:', error);
                    const message = (error && (error.detail || error.message)) || 'Error uploading file';
                    toast.error(message, toastConfig);
                    setUploading(false);
                });
        } else {
            toast.error('Please select a file to upload', toastConfig);
        }
    }

    function deleteFileAssociation(fileId: string) {
        remove(`/agents/${agentId}/files/${fileId}`)
            .then(() => {
                toast.success('File removed from agent', toastConfig);
                setFiles((prevFiles) => prevFiles.filter((file: any) => file.id !== fileId));
                
                captureEvent('file_removed', {
                    agent_id: agentId,
                    file_id: fileId
                });
            })
            .catch((error) => {
                console.error('Error removing file:', error);
                toast.error('Error removing file', toastConfig);
            });
    }

    function openFileModal(file: any) {
        setSelectedFileContent(file.file_metadata?.text || 'No content available');
        setSelectedFileChunks(file.file_metadata?.chunks || []);
        setIsFileModalOpen(true);
    }

    function closeFileModal() {
        setIsFileModalOpen(false);
        setSelectedFileContent('');
        setSelectedFileChunks([]);
    }

    const toggleFileExpansion = (fileId: string) => {
        setExpandedFiles(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(fileId)) {
                newExpanded.delete(fileId);
            } else {
                newExpanded.add(fileId);
            }
            return newExpanded;
        });
    };

    return (
        <div className="flex w-full max-w-full flex-col space-y-8">
            {/* Upload document section */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Upload document</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Add a PDF or Word document to this agent's knowledge.
                    </p>
                </div>

                <div className="space-y-4 rounded-lg border bg-card p-4">
                        <div className="min-w-0 space-y-1">
                            <h4 className="text-sm font-medium">Upload a File</h4>
                            <p className="text-xs text-muted-foreground">Upload a PDF or Word document to train your agent with additional knowledge. Supported formats: PDF, DOCX.</p>
                        </div>

                    {uploading && (
                        <p className="text-sm text-muted-foreground animate-pulse">
                            If your file has a lot of content, this may take a few moments
                        </p>
                    )}

                    <div className="flex flex-row space-x-2 items-center">
                        <div className="flex-1 flex items-center">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.docx,.doc,.txt,.md"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            <Button
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                className="mr-2"
                            >
                                Choose File
                            </Button>
                            <div className="flex-1 overflow-hidden">
                                {selectedFile ? (
                                    <div className="text-sm truncate">
                                        {selectedFile.name}
                                    </div>
                                ) : (
                                    <div className="text-sm text-muted-foreground">
                                        No file selected
                                    </div>
                                )}
                            </div>
                        </div>
                        <Button
                            disabled={!selectedFile || uploading}
                            className="flex items-center justify-center"
                            onClick={handleUploadFile}
                        >
                            {uploading ? (
                                <div className="inline-flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-600 dark:text-slate-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Uploading...
                                </div>
                            ) : (
                                'Upload File'
                            )}
                        </Button>
                    </div>

                    {/* Add Existing File */}
                    <div className="rounded-lg border border-dashed p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0 basis-48">
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium">Add existing document</h4>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Link a document already uploaded on another agent to share it across agents.
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setShowFiles(true)}
                                className="shrink-0"
                            >
                                Browse documents
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Trained files section */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Trained documents</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Review and manage documents connected to this agent.
                    </p>
                </div>

                <div className="flex flex-col space-y-2 w-full">
                    {loading ? (
                        <div className="space-y-2">
                            <FileSkeleton />
                        </div>
                    ) : files.length > 0 ? (
                                files.map((file: any) => (
                                    <div key={file.id} className="flex flex-col w-full p-3 rounded-md border text-sm">
                                        <div className="w-full">
                                            <div className="flex flex-row justify-between w-full items-center">
                                                <div 
                                                    className="flex items-center overflow-hidden cursor-pointer flex-1"
                                                    onClick={() => toggleFileExpansion(file.id)}
                                                >
                                                    {expandedFiles.has(file.id) ? (
                                                        <ChevronDown className="w-5 h-5 mr-3 text-slate-600 flex-shrink-0 dark:text-slate-300" />
                                                    ) : (
                                                        <ChevronRight className="w-5 h-5 mr-3 text-slate-600 flex-shrink-0 dark:text-slate-300" />
                                                    )}
                                                    <div className="flex items-center overflow-hidden flex-1">
                                                        <div className="text-sm dark:text-slate-200 break-all font-medium overflow-hidden text-ellipsis">
                                                            {file.name}
                                                        </div>
                                                        {file.created_at && (
                                                            <div className="flex items-center text-xs text-muted-foreground ml-auto pl-2 whitespace-nowrap">
                                                                <Clock className="w-3 h-3 mr-1" />
                                                                {formatRelativeTime(file.created_at)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 ml-4">
                                                    <span className="text-xs px-2 py-0.5 rounded-md border bg-accent border-border text-green-600 dark:text-green-400">
                                                        {file.characters || 0} chars
                                                    </span>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button 
                                                                    variant="outline" 
                                                                    size="sm" 
                                                                    onClick={() => deleteFileAssociation(file.id)}
                                                                    className="flex items-center hover:text-red-500 flex-shrink-0"
                                                                >
                                                                    <Trash2 className="w-3 h-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                Remove this file from agent
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            </div>
                                    
                                        </div>
                                        
                                        {/* Expanded file content preview */}
                                        {expandedFiles.has(file.id) && (
                                            <div className="mt-3 pl-7 border-t pt-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openFileModal(file)}
                                                    className="w-full text-xs text-center mt-2"
                                                >
                                                    View Full Content
                                                </Button>
                                                <div className="relative mt-2 max-h-24 overflow-hidden rounded-md border p-3 text-xs text-muted-foreground">
                                                    {file.file_metadata?.text ? (
                                                        <>
                                                            <div className="line-clamp-3">
                                                                {file.file_metadata.text}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-center">Content preview not available</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="rounded-lg border border-dashed p-8 text-center">
                                    <div className="flex flex-col items-center gap-2">
                                        <p className="text-sm font-medium text-muted-foreground">No trained documents</p>
                                        <p className="text-xs text-muted-foreground">
                                            Upload a document above to train your agent with additional knowledge.
                                        </p>
                                    </div>
                                </div>
                            )}
                    </div>
                </div>
            
            {showFiles && (
                <FilesList
                    agentId={agentId}
                    setShowFiles={setShowFiles}
                    setFilesSelected={setFilesSelected}
                />
            )}

            {/* File Content Modal */}
            <Dialog open={isFileModalOpen} onOpenChange={closeFileModal}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center">
                            <File className="w-5 h-5 mr-2" />
                            File Content
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-hidden flex flex-col">
                        <div className="mt-2 flex-1 overflow-hidden">
                            <ScrollArea className="h-[40vh] rounded-md border p-4">
                                <div className="text-sm [white-space:pre-line]">
                                    {selectedFileContent}
                                </div>
                            </ScrollArea>
                        </div>

                        {/* <div className="mt-6">
                            <h4 className="text-md font-medium mb-4">
                                Chunks ({selectedFileChunks.length})
                            </h4>
                            <ScrollArea className="h-[30vh]">
                                <div className="space-y-4">
                                    {selectedFileChunks.map((chunk, index) => (
                                        <Card key={chunk.id}>
                                            <CardContent className="p-4">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="text-xs font-medium">
                                                        Chunk {index + 1} • {chunk.tokens} tokens
                                                    </span>
                                                    <span className="text-xs font-medium">
                                                        ID: {chunk.id.substring(0, 8)}...
                                                    </span>
                                                </div>
                                                <div className="text-sm [white-space:pre-line]">
                                                    {chunk.text}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div> */}
                    </div>

                    <DialogFooter>
                        <Button onClick={closeFileModal}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}